import { RUNE_IDS, SPELL_IDS, type RuneDef, type RuneId, type SpellId } from '../types';

/**
 * Spell runes (DROW-140 spec, final). Every number is a first-pass value to tune in playtests.
 * `overrides` replace level-1 fields; the spell's `perLevel` (or the rune's own) is added on top
 * (`spellStats`). Behaviour params don't scale with the level.
 */
export const RUNES = {
  // Q · Fireball
  detonation: {
    id: 'detonation',
    spellId: 'fireball',
    name: 'Detonation',
    blurb: 'Flies to the target point and explodes there. Passes through warlocks.',
    cost: 8,
    overrides: { damage: 8, force: 380, speed: 700, cooldownMs: 2500 },
    behaviour: { tag: 'detonateAtTarget', blastRadius: 90 },
  },
  searingBolt: {
    id: 'searingBolt',
    spellId: 'fireball',
    name: 'Searing Bolt',
    blurb: 'A thin, fast poke: more damage, less push.',
    cost: 6,
    overrides: { damage: 14, force: 340, speed: 1300, radius: 10, range: 750 },
    behaviour: null,
  },
  kindling: {
    id: 'kindling',
    spellId: 'fireball',
    name: 'Kindling',
    blurb: 'Sets the target burning for 3 s. Less push now, more damage later.',
    cost: 6,
    overrides: { damage: 6, force: 360 },
    behaviour: { tag: 'burn', dps: 3, durationMs: 3000 },
  },
  // W · Homing
  seeker: {
    id: 'seeker',
    spellId: 'homing',
    name: 'Seeker',
    blurb: 'Wider lock-on and tighter turns, but slow and weaker.',
    cost: 8,
    overrides: { detectRadius: 600, turnRate: 5.0, speed: 420, force: 300 },
    behaviour: null,
  },
  ambush: {
    id: 'ambush',
    spellId: 'homing',
    name: 'Ambush',
    blurb: 'Flies straight for 0.6 s, then lights up, speeds up and homes.',
    cost: 8,
    overrides: { detectRadius: 450, force: 420, cooldownMs: 8000 },
    behaviour: { tag: 'delayedHoming', straightMs: 600, boostSpeed: 800 },
  },
  splinter: {
    id: 'splinter',
    spellId: 'homing',
    name: 'Splinter',
    blurb: 'Bursts into 3 fragments on a hit or at max range.',
    cost: 8,
    overrides: { damage: 8, force: 300, cooldownMs: 8000 },
    behaviour: {
      tag: 'splitOnEnd',
      count: 3,
      fanDeg: 60,
      damage: 4,
      force: 180,
      radius: 10,
      range: 250,
      speed: 700,
    },
  },
  // E · Lightning
  chain: {
    id: 'chain',
    spellId: 'lightning',
    name: 'Chain',
    blurb: 'After a hit, jumps to the nearest other enemy within 250.',
    cost: 8,
    overrides: { force: 320, cooldownMs: 6000 },
    behaviour: { tag: 'chain', jumpRange: 250, damage: 4, force: 180 },
  },
  overcharge: {
    id: 'overcharge',
    spellId: 'lightning',
    name: 'Overcharge',
    blurb: 'A 0.25 s telegraphed windup, then a short, heavy bolt.',
    cost: 8,
    overrides: { force: 480, range: 400 },
    behaviour: { tag: 'windup', windupMs: 250 },
  },
  pierce: {
    id: 'pierce',
    spellId: 'lightning',
    name: 'Pierce',
    blurb: 'Hits every warlock on the line, each for less.',
    cost: 8,
    overrides: { damage: 6, force: 260 },
    behaviour: { tag: 'pierce' },
  },
  // R · Meteor
  comet: {
    id: 'comet',
    spellId: 'meteor',
    name: 'Comet',
    blurb: 'Lands faster on a smaller area.',
    cost: 10,
    overrides: { delayMs: 500, radius: 80, damage: 16 },
    behaviour: null,
  },
  cataclysm: {
    id: 'cataclysm',
    spellId: 'meteor',
    name: 'Cataclysm',
    blurb: 'A huge, slow blast with weaker push.',
    cost: 10,
    overrides: { radius: 180, delayMs: 1300, force: 560 },
    behaviour: null,
  },
  magmaPool: {
    id: 'magmaPool',
    spellId: 'meteor',
    name: 'Magma Pool',
    blurb: 'Leaves a lava pool for 3 s that burns enemies.',
    cost: 10,
    overrides: { damage: 14, force: 450 },
    behaviour: { tag: 'lavaPool', radius: 100, durationMs: 3000 },
  },
  // D · Teleport
  blinkStrike: {
    id: 'blinkStrike',
    spellId: 'teleport',
    name: 'Blink Strike',
    blurb: 'A shockwave on arrival knocks enemies away.',
    cost: 8,
    overrides: { range: 380, cooldownMs: 14000 },
    behaviour: { tag: 'arrivalBlast', radius: 90, damage: 4, force: 300 },
  },
  recall: {
    id: 'recall',
    spellId: 'teleport',
    name: 'Recall',
    blurb: 'Press again within 3 s to return to where you blinked from.',
    cost: 8,
    overrides: { cooldownMs: 15000 },
    behaviour: { tag: 'recall', windowMs: 3000 },
  },
  quickstep: {
    id: 'quickstep',
    spellId: 'teleport',
    name: 'Quickstep',
    blurb: 'Short and frequent.',
    cost: 8,
    overrides: { range: 250, cooldownMs: 7000 },
    perLevel: { cooldownMs: -500, range: 25 },
    behaviour: null,
  },
  // D · Thrust
  batteringRam: {
    id: 'batteringRam',
    spellId: 'thrust',
    name: 'Battering Ram',
    blurb: 'Stops at the first warlock for a huge hit; you take 30% recoil.',
    cost: 8,
    overrides: { force: 620, damage: 12, cooldownMs: 11000 },
    behaviour: { tag: 'stopAtFirst', recoilFrac: 0.3 },
  },
  slipstream: {
    id: 'slipstream',
    spellId: 'thrust',
    name: 'Slipstream',
    blurb: 'A pure escape: passes through warlocks, no damage or push.',
    cost: 8,
    overrides: { range: 520, cooldownMs: 8000, damage: 0, force: 0 },
    perLevel: { cooldownMs: -1000 },
    behaviour: { tag: 'phaseThrough' },
  },
  // F · Shield
  emberward: {
    id: 'emberward',
    spellId: 'shield',
    name: 'Emberward',
    blurb: 'No reflect: lava immunity and 40% knockback resist while up.',
    cost: 8,
    overrides: { durationMs: 2500, cooldownMs: 14000 },
    behaviour: { tag: 'lavaWard', kbResist: 0.4 },
  },
  mirror: {
    id: 'mirror',
    spellId: 'shield',
    name: 'Mirror',
    blurb: 'A tight reflect: reflected shots gain 50% speed and 30% force.',
    cost: 8,
    overrides: { durationMs: 500, cooldownMs: 9000 },
    perLevel: { cooldownMs: -500, durationMs: 100 },
    behaviour: { tag: 'empoweredReflect', speedMult: 1.5, forceMult: 1.3 },
  },
  repulse: {
    id: 'repulse',
    spellId: 'shield',
    name: 'Repulse',
    blurb: 'A shorter reflect; a nova pushes enemies away when it ends.',
    cost: 8,
    overrides: { durationMs: 1000 },
    behaviour: { tag: 'endNova', radius: 140, damage: 0, force: 380 },
  },
  // C · Link
  reel: {
    id: 'reel',
    spellId: 'link',
    name: 'Reel',
    blurb: 'Pulls the target to you instead. Stones do nothing.',
    cost: 8,
    overrides: { pullSpeed: 700, durationMs: 1500, cooldownMs: 13000 },
    behaviour: { tag: 'reverseTether' },
  },
  grapple: {
    id: 'grapple',
    spellId: 'link',
    name: 'Grapple',
    blurb: 'Longer and faster for mobility; the hook deals no damage.',
    cost: 8,
    overrides: { range: 850, speed: 1000, pullSpeed: 1100, damage: 0, cooldownMs: 12000 },
    behaviour: null,
  },
  snap: {
    id: 'snap',
    spellId: 'link',
    name: 'Snap',
    blurb: 'Reaching the target knocks it along your travel direction.',
    cost: 8,
    overrides: { damage: 0, durationMs: 1500, pullSpeed: 700 },
    behaviour: { tag: 'arrivalKnock', force: 400 },
  },
} as const satisfies Record<RuneId, RuneDef>;

/** Each spell's runes, in `RUNE_IDS` order. */
export const RUNES_BY_SPELL: Readonly<Record<SpellId, readonly RuneId[]>> = Object.fromEntries(
  SPELL_IDS.map((spellId) => [spellId, RUNE_IDS.filter((id) => RUNES[id].spellId === spellId)]),
) as unknown as Record<SpellId, readonly RuneId[]>;

export function isRuneId(value: unknown): value is RuneId {
  return typeof value === 'string' && (RUNE_IDS as readonly string[]).includes(value);
}

export function getRune(id: RuneId): RuneDef {
  return RUNES[id];
}

/**
 * The rune a synced slot/projectile string names, if it is a real rune for `spellId`; else null.
 * The sim reads every rune through this, so a stale or mismatched id plays the base spell.
 */
export function runeFor(spellId: SpellId, rune: string | null | undefined): RuneDef | null {
  if (!isRuneId(rune)) return null;
  const def: RuneDef = RUNES[rune];
  return def.spellId === spellId ? def : null;
}
